import { jsx } from 'react/jsx-runtime';
import { Keyboard } from '@capacitor/keyboard';
import { isInMobile } from '@fabric-app/compat';
import { useContext, createContext, useState, useCallback, useEffect } from 'react';
import useVisualViewport from '@fabric-app/ui/hooks/useVisualViewport';
import { isEditableElement } from '@fabric-app/utils/element';
import { isFunction } from '@fabric-app/utils/guards';
import { dispatchFabricEvent } from '../hooks/useFabricEvent.js';
import equal from 'fast-deep-equal';
import { useWindowEvent } from '@fabric-app/hooks/useWindowEvent';

const VirtualKeyboardContext = /* @__PURE__ */ createContext({
  isVisible: false,
  height: 0,
  visualViewport: null,
  method: "none"
});
const useVirtualKeyboardContext = () => {
  return useContext(VirtualKeyboardContext);
};
const VirtualKeyboardProvider = ({ children }) => {
  const [state, _setState] = useState({
    isVisible: false,
    height: 0,
    visualViewport: null,
    method: "none"
  });
  const setState = useCallback((value) => {
    _setState((prev) => {
      const v = isFunction(value) ? value(prev) : value;
      const isEqual = equal(prev, v);
      if (isEqual) return prev;
      dispatchFabricEvent("fabric-keyboard-event", {
        isVisible: v.isVisible,
        height: v.height,
        method: v.method
      });
      return v;
    });
  }, []);
  const viewport = useVisualViewport({
    recalculateKey: state.height
  });
  useEffect(() => {
    if (!isInMobile()) return;
    const handleKeyboardShow = (info) => {
      setState({
        isVisible: true,
        height: info.keyboardHeight,
        visualViewport: null,
        method: "capacitor"
      });
      document.body.dataset.keyboardOpen = "true";
    };
    const handleKeyboardHide = () => {
      setState({
        isVisible: false,
        height: 0,
        visualViewport: null,
        method: "none"
      });
      document.body.dataset.keyboardOpen = "false";
    };
    Keyboard.addListener("keyboardWillShow", handleKeyboardShow);
    Keyboard.addListener("keyboardDidShow", handleKeyboardShow);
    Keyboard.addListener("keyboardWillHide", handleKeyboardHide);
    Keyboard.addListener("keyboardDidHide", handleKeyboardHide);
    return () => {
      Keyboard.removeAllListeners();
    };
  }, [
    setState
  ]);
  useEffect(() => {
    if (!window.fabricMobile) return;
    window.fabricMobile.onKeyboardStateUpdate = (visible, height) => {
      setState({
        isVisible: visible,
        height,
        visualViewport: null,
        method: "fabric-sheet"
      });
    };
    return () => {
      if (window.fabricMobile) window.fabricMobile.onKeyboardStateUpdate = void 0;
    };
  }, [
    setState
  ]);
  useEffect(() => {
    if (isInMobile() || window.fabricMobile || !viewport) return;
    const height = viewport.heightCrop;
    if (height > 0 && document.activeElement && isEditableElement(document.activeElement, true)) {
      setState({
        isVisible: true,
        height,
        visualViewport: viewport,
        method: "visual-viewport"
      });
    } else {
      setState({
        isVisible: false,
        height: 0,
        visualViewport: null,
        method: "visual-viewport"
      });
    }
  }, [
    viewport,
    setState
  ]);
  const updateVisualViewport = useCallback(() => {
    if (isInMobile() || window.fabricMobile || typeof window.visualViewport === "undefined" || !window.visualViewport) return;
    const visualViewport = window.visualViewport;
    const height = Math.max(Math.floor(window.innerHeight - visualViewport.height), 0);
    if (height <= 0 || !document.activeElement || !isEditableElement(document.activeElement, true)) return;
    setState({
      isVisible: true,
      height,
      visualViewport: {
        width: visualViewport.width,
        height: visualViewport.height,
        offsetLeft: visualViewport.offsetLeft,
        offsetTop: visualViewport.offsetTop,
        pageLeft: visualViewport.pageLeft,
        pageTop: visualViewport.pageTop,
        scale: visualViewport.scale,
        heightCrop: height,
        windowActualHeight: window.innerHeight,
        windowActualWidth: window.innerWidth
      },
      method: "visual-viewport"
    });
  }, [
    setState
  ]);
  useWindowEvent("scroll", (event) => {
    if (event.target !== document.documentElement && event.target !== document.body && event.target !== document.scrollingElement && event.target !== document) return;
    if (typeof window.visualViewport === "undefined" || !window.visualViewport || window.visualViewport.height >= window.innerHeight) return;
    event.preventDefault();
    window.scrollTo(0, 0);
    updateVisualViewport();
  }, {
    enabled: true,
    capture: true,
    passive: false
  });
  useWindowEvent("resize", () => {
    if (typeof window.visualViewport === "undefined" || !window.visualViewport || window.visualViewport.height >= window.innerHeight) return;
    window.scrollTo(0, 0);
    updateVisualViewport();
  }, {
    enabled: true,
    capture: true,
    passive: false
  });
  return /* @__PURE__ */ jsx(VirtualKeyboardContext.Provider, {
    value: state,
    children
  });
};

export { VirtualKeyboardProvider, useVirtualKeyboardContext };
//# sourceMappingURL=keyboard.context.js.map
